import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Input } from "semantic-ui-react";

import "components/FileDragDrop/FileDragDrop.scss";
import { getFilesWithinSizeLimit } from "utils/common";

type DragEvent = React.DragEvent<HTMLDivElement>;

type FileDragDropProps = {
  id: string;
  required?: boolean;
  disabled?: boolean;
  onFileUpload: (files: File[]) => void;
};

export const FileDragDrop = ({ onFileUpload, id, required = true, disabled = false }: FileDragDropProps) => {
  const { t } = useTranslation();

  const [draggedOver, setDraggedOver] = useState(false);

  const handleDrop = (evt: DragEvent) => {
    if (disabled) return;
    evt.preventDefault();
    setDraggedOver(false);

    const files = getFilesWithinSizeLimit([...evt.dataTransfer.files], t);

    if (files.length > 0) onFileUpload(files);
  };

  const handleAddFile = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const files = getFilesWithinSizeLimit([...(evt.target.files ?? [])], t);
    evt.target.value = "";

    if (files.length > 0) onFileUpload(files);
  };

  const handleDragEnter = (e: DragEvent) => {
    e.preventDefault();
    if (disabled) return;
    setDraggedOver(true);
  };

  const handleDragLeave = () => {
    if (disabled) return;

    setDraggedOver(false);
  };

  const handleDragOver = (e: DragEvent) => {
    if (disabled) return;

    e.preventDefault();
  };

  const triggerOnChangeOnFileInput = () => {
    if (disabled) return;
    document.getElementById(id)?.click();
  };

  return (
    <div
      className={`FileDragDrop ${draggedOver ? "active" : ""}`}
      onDrop={handleDrop}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
    >
      <div>
        <span>
          {t("v8_drag_files_here", "Drag files here")} {t("v8_or", "or")}
        </span>{" "}
        <span className="browse-text" onClick={triggerOnChangeOnFileInput}>
          {t("v8_browse", "Browse")}
        </span>
      </div>
      <div className="max-file-size">{t("v8_maximum_file_size", "Maximum file size")} 32MB</div>
      <Input type="file" disabled={disabled} id={id} onChange={handleAddFile} placeholder="Browse" multiple required={required} />
    </div>
  );
};
